import Phaser from 'phaser';
import { CombatSystem } from '../systems/CombatSystem';
import { MovementSystem } from '../systems/MovementSystem';
import { Rocket } from './Rocket';

export enum EnemyState {
  PATROL = 'PATROL',
  CHASE = 'CHASE',
  SEEK_COVER = 'SEEK_COVER',
  HOLD = 'HOLD',
  DEAD = 'DEAD',
}

export enum EnemyType {
  RIFLEMAN = 'RIFLEMAN',
  ROCKET_TROOPER = 'ROCKET_TROOPER',
}

const TILE_SIZE = 32;
const CHASE_RANGE = 200;
const HEARING_RADIUS = 150; // px — range at which enemy hears gunfire
const CHASE_SPEED = 60; // px/s
const PATROL_SPEED = 40; // px/s
const PATROL_RANGE = 3; // tiles
const BULLET_COVER_RADIUS = 100; // px — bullet proximity that triggers cover-seek
const COVER_CHECK_RADIUS = 4; // tiles to search for cover
const CHASE_STOP_TILES = 1; // tiles to leave between enemy and chase target (prevents overlap)

// Per-type fire config
const FIRE_CONFIG: Record<EnemyType, { interval: number; damage: number }> = {
  [EnemyType.RIFLEMAN]: { interval: 1500, damage: 10 },
  [EnemyType.ROCKET_TROOPER]: { interval: 3000, damage: 30 },
};

export class Enemy extends Phaser.GameObjects.Sprite {
  health: number = 3;
  state: EnemyState = EnemyState.PATROL;
  readonly enemyType: EnemyType;

  /** Set by EnemyManager — if false, hold position instead of chasing */
  isActiveChaser: boolean = false;

  private spawnTileY: number;
  private patrolMinX: number;
  private patrolMaxX: number;
  private patrolDir: number = 1;
  private fireTimer: number = 0;
  private combatSystem: CombatSystem;
  private movementSystem: MovementSystem;
  private mapWidth: number;
  private mapHeight: number;

  // Pathfinding chase
  private currentPath: { x: number; y: number }[] = [];
  private isMovingAlongPath: boolean = false;
  private pathfindCooldown: number = 0;
  private readonly PATHFIND_INTERVAL = 800; // ms between re-path

  // Waypoint patrol
  private patrolWaypoints: { x: number; y: number }[] = []; // world pixel coords
  private patrolWaypointIndex: number = 0;
  private patrolLoopMode: 'loop' | 'ping_pong' | 'once' = 'loop';
  private patrolPingDir: number = 1; // 1 = forward, -1 = backward (for ping_pong)

  // Cover-seek
  private coverTarget: { tx: number; ty: number } | null = null;
  private groundLayer: Phaser.Tilemaps.TilemapLayer | null = null;

  // Rockets from enemy
  private rockets: Rocket[] = [];

  constructor(
    scene: Phaser.Scene,
    tileX: number,
    tileY: number,
    combatSystem: CombatSystem,
    movementSystem: MovementSystem,
    mapWidth: number,
    mapHeight: number,
    enemyType: EnemyType = EnemyType.RIFLEMAN,
    groundLayer: Phaser.Tilemaps.TilemapLayer | null = null
  ) {
    const worldX = tileX * TILE_SIZE + TILE_SIZE / 2;
    const worldY = tileY * TILE_SIZE + TILE_SIZE / 2;
    super(scene, worldX, worldY, 'soldier');
    scene.add.existing(this);

    this.spawnTileY = tileY;
    this.combatSystem = combatSystem;
    this.movementSystem = movementSystem;
    this.mapWidth = mapWidth;
    this.mapHeight = mapHeight;
    this.enemyType = enemyType;
    this.groundLayer = groundLayer;

    this.patrolMinX = (tileX - PATROL_RANGE) * TILE_SIZE + TILE_SIZE / 2;
    this.patrolMaxX = (tileX + PATROL_RANGE) * TILE_SIZE + TILE_SIZE / 2;

    // Visual distinction
    if (enemyType === EnemyType.ROCKET_TROOPER) {
      this.setTint(0xaa44ff); // purple tint for rocket troopers
    } else {
      this.setTint(0xff4444); // red tint for riflemen
    }

    this.setDepth(10);
    this.play('soldier-idle');
  }

  isDead(): boolean {
    return this.state === EnemyState.DEAD;
  }

  setPatrolPath(waypoints: { x: number; y: number }[], loopMode: 'loop' | 'ping_pong' | 'once' = 'loop'): void {
    this.patrolWaypoints = waypoints;
    this.patrolLoopMode = loopMode;
    this.patrolWaypointIndex = 0;
    this.patrolPingDir = 1;
  }

  takeDamage(amount: number): void {
    if (this.isDead()) return;
    this.health -= amount;
    if (this.health <= 0) {
      this.die();
    } else {
      const origTint = this.enemyType === EnemyType.ROCKET_TROOPER ? 0xaa44ff : 0xff4444;
      this.setTint(0xffffff);
      this.scene.time.delayedCall(100, () => {
        if (!this.isDead()) this.setTint(origTint);
      });
    }
  }

  private die(): void {
    this.state = EnemyState.DEAD;
    this.isActiveChaser = false;
    // Destroy any in-flight enemy rockets
    for (const r of this.rockets) {
      if (r.active) r.destroy();
    }
    this.rockets = [];

    // Guard: scene may be null if the game object was already destroyed
    if (!this.scene) return;

    // Notify scene for audio / kill count / VFX
    this.scene.events.emit('enemyDied', this.x, this.y);

    this.setTint(0xff0000);
    this.scene.tweens.add({
      targets: this,
      alpha: 0,
      duration: 500,
      ease: 'Power2',
      onComplete: () => this.destroy(),
    });
  }

  /**
   * Notify this enemy that a bullet passed nearby (for cover-seeking).
   */
  private trySeekCover(): void {
    if (!this.groundLayer) return;
    const tileX = Math.floor(this.x / TILE_SIZE);
    const tileY = Math.floor(this.y / TILE_SIZE);

    // Scan nearby tiles for cover (tile index 2 = wall, adjacent walkable tile)
    let best: { tx: number; ty: number } | null = null;
    let bestDist = Infinity;

    for (let dy = -COVER_CHECK_RADIUS; dy <= COVER_CHECK_RADIUS; dy++) {
      for (let dx = -COVER_CHECK_RADIUS; dx <= COVER_CHECK_RADIUS; dx++) {
        const tx = tileX + dx;
        const ty = tileY + dy;
        const tile = this.groundLayer.getTileAt(tx, ty);
        if (!tile || tile.index !== 2) continue;

        // Check adjacent walkable tile (prefer one step away from wall)
        const adjacents = [
          { x: tx + 1, y: ty }, { x: tx - 1, y: ty },
          { x: tx, y: ty + 1 }, { x: tx, y: ty - 1 },
        ];
        for (const adj of adjacents) {
          const adjTile = this.groundLayer.getTileAt(adj.x, adj.y);
          if (!adjTile || adjTile.index === 2) continue;
          const worldX = adj.x * TILE_SIZE + TILE_SIZE / 2;
          const worldY = adj.y * TILE_SIZE + TILE_SIZE / 2;
          const dist = Phaser.Math.Distance.Between(this.x, this.y, worldX, worldY);
          if (dist < bestDist) {
            bestDist = dist;
            best = { tx: adj.x, ty: adj.y };
          }
        }
      }
    }

    if (best) {
      this.coverTarget = best;
      this.state = EnemyState.SEEK_COVER;
      this.isMovingAlongPath = false;
      this.currentPath = [];
    }
  }

  update(
    delta: number,
    playerX: number,
    playerY: number,
    bullets?: { x: number; y: number }[]
  ): void {
    if (this.isDead()) return;

    // Update enemy rockets
    for (let i = this.rockets.length - 1; i >= 0; i--) {
      const r = this.rockets[i];
      if (!r.active) { this.rockets.splice(i, 1); continue; }
      r.update(delta);
    }

    // Check for nearby bullets → trigger cover seek
    if (bullets) {
      for (const b of bullets) {
        const dist = Phaser.Math.Distance.Between(this.x, this.y, b.x, b.y);
        if (dist <= BULLET_COVER_RADIUS) {
          this.trySeekCover();
          break;
        }
      }
    }

    const distToPlayer = Phaser.Math.Distance.Between(this.x, this.y, playerX, playerY);

    // State transitions
    if (this.state !== EnemyState.SEEK_COVER && this.state !== EnemyState.DEAD) {
      if (distToPlayer <= CHASE_RANGE && this.isActiveChaser) {
        this.state = EnemyState.CHASE;
      } else if (distToPlayer <= CHASE_RANGE && !this.isActiveChaser) {
        this.state = EnemyState.HOLD;
      } else {
        this.state = EnemyState.PATROL;
      }
    }

    switch (this.state) {
      case EnemyState.CHASE:
        this.updateChasePathfinding(delta, playerX, playerY);
        this.tryFire(delta, playerX, playerY);
        break;
      case EnemyState.HOLD:
        // Hold position; still fire if in range
        this.tryFire(delta, playerX, playerY);
        break;
      case EnemyState.PATROL:
        this.updatePatrol(delta);
        break;
      case EnemyState.SEEK_COVER:
        this.updateSeekCover(delta, playerX, playerY);
        break;
    }
  }

  private updateChasePathfinding(delta: number, playerX: number, playerY: number): void {
    this.pathfindCooldown -= delta;

    if (!this.isMovingAlongPath && this.pathfindCooldown <= 0) {
      this.pathfindCooldown = this.PATHFIND_INTERVAL;
      // findPath takes and returns world-pixel coordinates
      this.movementSystem.findPath({ x: this.x, y: this.y }, { x: playerX, y: playerY }, (path) => {
        if (this.isDead()) return;
        if (path && path.length > 1) {
          // Skip first waypoint (current position).
          // Trim waypoints that are within CHASE_STOP_TILES * TILE_SIZE of the target
          // to avoid sprite overlap with the player.
          const stopDist = CHASE_STOP_TILES * TILE_SIZE;
          let trimEnd = path.length;
          while (trimEnd > 1 &&
            Phaser.Math.Distance.Between(path[trimEnd - 1].x, path[trimEnd - 1].y, playerX, playerY) < stopDist) {
            trimEnd--;
          }
          this.currentPath = path.slice(1, trimEnd);
          if (!this.isMovingAlongPath && this.currentPath.length > 0) this.stepAlongPath();
        }
      });
    }
  }

  private updateSeekCover(delta: number, playerX: number, playerY: number): void {
    if (!this.coverTarget) {
      this.state = EnemyState.CHASE;
      return;
    }

    if (!this.isMovingAlongPath) {
      // Cover target is stored as tile coords; convert to world pixels for findPath
      const coverWorldX = this.coverTarget.tx * TILE_SIZE + TILE_SIZE / 2;
      const coverWorldY = this.coverTarget.ty * TILE_SIZE + TILE_SIZE / 2;
      this.movementSystem.findPath(
        { x: this.x, y: this.y },
        { x: coverWorldX, y: coverWorldY },
        (path) => {
          if (this.isDead()) return;
          if (path && path.length > 1) {
            this.currentPath = path.slice(1);
            this.stepAlongPath();
          } else {
            // Can't reach cover — fall back
            this.coverTarget = null;
            this.state = EnemyState.CHASE;
          }
        }
      );
    }

    // Once arrived at cover, resume chase
    if (!this.isMovingAlongPath && this.coverTarget) {
      const wx = this.coverTarget.tx * TILE_SIZE + TILE_SIZE / 2;
      const wy = this.coverTarget.ty * TILE_SIZE + TILE_SIZE / 2;
      const dist = Phaser.Math.Distance.Between(this.x, this.y, wx, wy);
      if (dist < 8) {
        this.coverTarget = null;
        this.state = EnemyState.CHASE;
      }
    }

    // Still fire while at cover
    this.tryFire(delta, playerX, playerY);
  }

  private stepAlongPath(speed: number = CHASE_SPEED): void {
    if (this.isDead() || !this.scene) {
      this.isMovingAlongPath = false;
      return;
    }
    if (this.currentPath.length === 0) {
      this.isMovingAlongPath = false;
      return;
    }

    this.isMovingAlongPath = true;
    const next = this.currentPath.shift()!;
    // Path waypoints are in world-pixel coordinates
    const targetX = next.x;
    const targetY = next.y;

    const angle = Phaser.Math.Angle.Between(this.x, this.y, targetX, targetY);
    this.setRotation(angle + Math.PI / 2);

    const distance = Phaser.Math.Distance.Between(this.x, this.y, targetX, targetY);
    const duration = Math.round(distance / speed * 1000);

    this.scene.tweens.add({
      targets: this,
      x: targetX,
      y: targetY,
      duration,
      ease: 'Linear',
      onComplete: () => {
        this.isMovingAlongPath = false;
        if (this.currentPath.length > 0 && !this.isDead()) {
          this.stepAlongPath(speed);
        }
      },
    });
  }

  private updatePatrol(delta: number): void {
    if (this.patrolWaypoints.length > 0) {
      this.updateWaypointPatrol(delta);
      return;
    }

    const dt = delta / 1000;
    const moveX = PATROL_SPEED * this.patrolDir * dt;
    const nextX = this.x + moveX;

    // Reverse if the next position would be non-walkable (wall or out of bounds)
    if (!this.movementSystem.isWalkable(nextX, this.y)) {
      this.patrolDir = -this.patrolDir;
      return;
    }

    this.x = nextX;
    this.setRotation(this.patrolDir > 0 ? Math.PI / 2 : -Math.PI / 2);

    if (this.x >= this.patrolMaxX) { this.x = this.patrolMaxX; this.patrolDir = -1; }
    else if (this.x <= this.patrolMinX) { this.x = this.patrolMinX; this.patrolDir = 1; }

    this.y = this.spawnTileY * TILE_SIZE + TILE_SIZE / 2;
  }

  private updateWaypointPatrol(_delta: number): void {
    if (this.isMovingAlongPath) return; // wait for current step to finish

    const target = this.patrolWaypoints[this.patrolWaypointIndex];
    if (!target) return;

    const dist = Phaser.Math.Distance.Between(this.x, this.y, target.x, target.y);
    if (dist < 4) {
      this.advancePatrolWaypoint();
      return;
    }

    // Use pathfinding to the next waypoint so walls are respected.
    this.isMovingAlongPath = true; // guard against multiple simultaneous findPath calls
    this.movementSystem.findPath({ x: this.x, y: this.y }, target, (path) => {
      this.isMovingAlongPath = false;
      if (this.isDead()) return;
      if (path && path.length > 1) {
        this.currentPath = path.slice(1);
        this.stepAlongPath(PATROL_SPEED);
      } else {
        // No path to waypoint — skip it
        this.advancePatrolWaypoint();
      }
    });
  }

  private advancePatrolWaypoint(): void {
    if (this.patrolLoopMode === 'loop') {
      this.patrolWaypointIndex = (this.patrolWaypointIndex + 1) % this.patrolWaypoints.length;
    } else if (this.patrolLoopMode === 'ping_pong') {
      this.patrolWaypointIndex += this.patrolPingDir;
      if (this.patrolWaypointIndex >= this.patrolWaypoints.length) {
        this.patrolWaypointIndex = this.patrolWaypoints.length - 2;
        this.patrolPingDir = -1;
      } else if (this.patrolWaypointIndex < 0) {
        this.patrolWaypointIndex = 1;
        this.patrolPingDir = 1;
      }
    }
    // 'once' — just stay at last waypoint
  }

  private tryFire(delta: number, playerX: number, playerY: number): void {
    const cfg = FIRE_CONFIG[this.enemyType];
    this.fireTimer += delta;
    if (this.fireTimer >= cfg.interval) {
      this.fireTimer = 0;
      this.doFire(playerX, playerY);
    }
  }

  private doFire(playerX: number, playerY: number): void {
    const angle = Phaser.Math.Angle.Between(this.x, this.y, playerX, playerY);
    this.setRotation(angle + Math.PI / 2);

    if (this.enemyType === EnemyType.ROCKET_TROOPER) {
      // Rocket trooper fires slower rockets (smaller explosion radius handled by caller)
      this.scene.events.emit('enemyRocketLaunch', this.x, this.y);
      const rocket = new Rocket(
        this.scene,
        this.x, this.y,
        playerX, playerY,
        this.mapWidth, this.mapHeight,
        (ex, ey) => {
          // Emit event so GameScene can handle explosion
          this.scene.events.emit('enemyRocketExplode', ex, ey);
        }
      );
      // Slow down rocket trooper rockets to 60% speed
      rocket.velocityX *= 0.6;
      rocket.velocityY *= 0.6;
      this.rockets.push(rocket);
    } else {
      this.combatSystem.fireBullet(this.x, this.y, playerX, playerY, true);
    }
  }

  getRockets(): Rocket[] {
    return this.rockets;
  }
}

export const ENEMY_SIGHT_RADIUS = CHASE_RANGE;
export const ENEMY_HEARING_RADIUS = HEARING_RADIUS;
